/**
 * ConfigEdgeBrowserPage — DRR-110 Config Edge Graph Explorer
 *
 * Read-only tool for architects to browse the care graph edge structure.
 * Edges are configuration-defined and append-only per FERP — no create/delete UI.
 *
 * Ring classification:
 *   1 = Regulatory (MANDATED_BY, IMPLEMENTS)
 *   2 = Outcomes (IMPROVES, MITIGATES, REIMBURSES_VIA)
 *   3 = Operations
 *   4 = Evidence (EVIDENCED_BY)
 *
 * Route: /dev/config-edges (architect only)
 */

import { useState, useCallback, useMemo, useRef } from 'react';
import type { ConfigEdge } from '@/generated/graphql';

import {
  useConfigEdgesQuery,
  useConfigReverseEdgesQuery,
} from '@/generated/graphql';
import {
  IconAlertTriangle,
  IconArrowRight,
  IconBinaryTree,
  IconInfoCircle,
  IconSearch,
  IconTag,
} from '@tabler/icons-react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Code,
  Container,
  Grid,
  Group,
  Loader,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
// ---------------------------------------------------------------------------
// Query Response Types
// ---------------------------------------------------------------------------

interface ConfigEdgesData {
  configEdges: ConfigEdge[];
}

interface ConfigReverseEdgesData {
  configReverseEdges: ConfigEdge[];
}

// ---------------------------------------------------------------------------
// GraphQL Operations
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Edge type → Mantine color. Covers DRR-110 standard edge types. */
const EDGE_TYPE_COLORS: Record<string, string> = {
  MANDATED_BY: 'red',
  MANDATES: 'red',
  IMPLEMENTS: 'violet',
  IMPLEMENTED_BY: 'violet',
  IMPROVES: 'green',
  IMPROVED_BY: 'green',
  MITIGATES: 'orange',
  MITIGATED_BY: 'orange',
  REIMBURSES_VIA: 'blue',
  REIMBURSES: 'blue',
  EVIDENCED_BY: 'cyan',
  EVIDENCES: 'cyan',
  FULFILLS: 'teal',
  FULFILLED_BY: 'teal',
  REQUIRES_COMPETENCY: 'grape',
  COMPETENCY_REQUIRED_BY: 'grape',
};

const RING_LABELS: Record<number, string> = {
  1: 'Regulatory',
  2: 'Outcomes',
  3: 'Operations',
  4: 'Evidence',
};

const RING_COLORS: Record<number, string> = {
  1: 'red',
  2: 'green',
  3: 'blue',
  4: 'cyan',
};

const LEGEND_ITEMS: Array<{ edgeType: string; color: string }> = [
  { edgeType: 'MANDATED_BY', color: 'red' },
  { edgeType: 'IMPLEMENTS', color: 'violet' },
  { edgeType: 'IMPROVES', color: 'green' },
  { edgeType: 'MITIGATES', color: 'orange' },
  { edgeType: 'REIMBURSES_VIA', color: 'blue' },
  { edgeType: 'EVIDENCED_BY', color: 'cyan' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function edgeColor(edgeType: string): string {
  return EDGE_TYPE_COLORS[edgeType] ?? 'gray';
}

function ringLabel(ring: number): string {
  return RING_LABELS[ring] ?? `Ring ${ring}`;
}

function ringColor(ring: number): string {
  return RING_COLORS[ring] ?? 'gray';
}

function isTagRef(id: string): boolean {
  return id.startsWith('tag:');
}

// ---------------------------------------------------------------------------
// EdgeTypeLegend
// ---------------------------------------------------------------------------

function EdgeTypeLegend() {
  return (
    <Group gap="xs" wrap="wrap">
      {LEGEND_ITEMS.map(({ edgeType, color }) => (
        <Badge key={edgeType} color={color} variant="light" size="sm">
          {edgeType}
        </Badge>
      ))}
    </Group>
  );
}

// ---------------------------------------------------------------------------
// EdgeTable
// ---------------------------------------------------------------------------

interface EdgeTableProps {
  readonly edges: ConfigEdge[];
  readonly idColumn: 'targetId' | 'sourceId';
  readonly labelColumn: 'edgeType' | 'inverseType';
  readonly onNavigate: (configId: string) => void;
}

function EdgeTable({
  edges,
  idColumn,
  labelColumn,
  onNavigate,
}: Readonly<EdgeTableProps>) {
  if (edges.length === 0) {
    return (
      <Text size="sm" c="dimmed" ta="center" py="md">
        No edges found.
      </Text>
    );
  }

  return (
    <Table striped highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Edge Type</Table.Th>
          <Table.Th>Config ID</Table.Th>
          <Table.Th>Ring</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {edges.map((edge, idx) => {
          const targetOrSource = edge[idColumn];
          const typeLabel = edge[labelColumn];
          return (
            <Table.Tr key={`${edge.sourceId}-${edge.targetId}-${idx}`}>
              <Table.Td>
                <Badge color={edgeColor(typeLabel)} variant="light" size="sm">
                  {typeLabel}
                </Badge>
              </Table.Td>
              <Table.Td>
                {isTagRef(targetOrSource) ? (
                  <Group gap="xs">
                    <IconTag size={12} color="gray" />
                    <Code>{targetOrSource}</Code>
                  </Group>
                ) : (
                  <Button
                    variant="subtle"
                    size="compact-xs"
                    rightSection={<IconArrowRight size={12} />}
                    onClick={() => onNavigate(targetOrSource)}
                    styles={{ root: { fontFamily: 'monospace', padding: '2px 6px' } }}
                  >
                    {targetOrSource}
                  </Button>
                )}
              </Table.Td>
              <Table.Td>
                <Badge color={ringColor(edge.ring)} variant="dot" size="sm">
                  {ringLabel(edge.ring)}
                </Badge>
              </Table.Td>
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    </Table>
  );
}

// ---------------------------------------------------------------------------
// EdgePanels — runs both forward + reverse queries in parallel
// ---------------------------------------------------------------------------

interface EdgePanelsProps {
  readonly configId: string;
  readonly onNavigate: (configId: string) => void;
}

function EdgePanels({ configId, onNavigate }: Readonly<EdgePanelsProps>) {
  const { data: forwardData, loading: forwardLoading, error: forwardError } =
    useConfigEdgesQuery({
      variables: { configId },
      fetchPolicy: 'network-only',
    });

  const { data: reverseData, loading: reverseLoading, error: reverseError } =
    useConfigReverseEdgesQuery({
      variables: { configId },
      fetchPolicy: 'network-only',
    });

  const forwardEdges: ConfigEdge[] = useMemo(
    () => forwardData?.configEdges ?? [],
    [forwardData],
  );

  const reverseEdges: ConfigEdge[] = useMemo(
    () => reverseData?.configReverseEdges ?? [],
    [reverseData],
  );

  return (
    <Grid mt="md">
      {/* Forward edges — what this config depends on */}
      <Grid.Col span={{ base: 12, md: 6 }}>
        <Card withBorder p="md">
          <Group justify="space-between" mb="sm">
            <Title order={5}>Dependencies (Forward Edges)</Title>
            {forwardLoading && <Loader size="xs" />}
            <Badge variant="light" size="sm">{forwardEdges.length}</Badge>
          </Group>
          <Text size="xs" c="dimmed" mb="sm">
            What <Code>{configId}</Code> depends on
          </Text>

          {forwardError && (
            <Alert color="red" icon={<IconAlertTriangle size={14} />} mb="sm">
              {forwardError.message}
            </Alert>
          )}

          <EdgeTable
            edges={forwardEdges}
            idColumn="targetId"
            labelColumn="edgeType"
            onNavigate={onNavigate}
          />
        </Card>
      </Grid.Col>

      {/* Reverse edges — what depends on this config */}
      <Grid.Col span={{ base: 12, md: 6 }}>
        <Card withBorder p="md">
          <Group justify="space-between" mb="sm">
            <Title order={5}>Dependents (Reverse Edges)</Title>
            {reverseLoading && <Loader size="xs" />}
            <Badge variant="light" size="sm">{reverseEdges.length}</Badge>
          </Group>
          <Text size="xs" c="dimmed" mb="sm">
            What depends on <Code>{configId}</Code>
          </Text>

          {reverseError && (
            <Alert color="red" icon={<IconAlertTriangle size={14} />} mb="sm">
              {reverseError.message}
            </Alert>
          )}

          <EdgeTable
            edges={reverseEdges}
            idColumn="sourceId"
            labelColumn="inverseType"
            onNavigate={onNavigate}
          />
        </Card>
      </Grid.Col>
    </Grid>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export function ConfigEdgeBrowserPage() {
  const [inputValue, setInputValue] = useState('');
  const [activeConfigId, setActiveConfigId] = useState<string | null>(null);
  const [history, setHistory] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleLoad = useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    setActiveConfigId(trimmed);
    setHistory((prev) => {
      // Deduplicate: if already last, don't push again
      if (prev[prev.length - 1] === trimmed) return prev;
      return [...prev, trimmed];
    });
  }, [inputValue]);

  const handleNavigate = useCallback((configId: string) => {
    setInputValue(configId);
    setActiveConfigId(configId);
    setHistory((prev) => {
      if (prev[prev.length - 1] === configId) return prev;
      return [...prev, configId];
    });
    // Scroll to top of page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleHistoryClick = useCallback(
    (configId: string) => {
      setInputValue(configId);
      setActiveConfigId(configId);
      // Trim history to the clicked entry (navigate "back")
      setHistory((prev) => {
        const idx = prev.lastIndexOf(configId);
        if (idx === -1) return prev;
        return prev.slice(0, idx + 1);
      });
    },
    [],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') handleLoad();
    },
    [handleLoad],
  );

  const isTagActive = activeConfigId ? isTagRef(activeConfigId) : false;

  return (
    <Container size="xl" py="xl">
      {/* Header */}
      <Group justify="space-between" mb="lg">
        <div>
          <Title order={2}>Config Edge Browser</Title>
          <Text size="sm" c="dimmed">
            DRR-110 care graph explorer — read-only. Edges are configuration-defined and append-only.
          </Text>
        </div>
        <Badge variant="light" color="violet" size="lg">Architect</Badge>
      </Group>

      {/* Edge type legend */}
      <Card withBorder p="sm" mb="md">
        <Group gap="xs" mb="xs">
          <IconBinaryTree size={14} />
          <Text size="xs" fw={600} c="dimmed">EDGE TYPE LEGEND</Text>
        </Group>
        <EdgeTypeLegend />
        <Group gap="md" mt="xs">
          {Object.entries(RING_LABELS).map(([ring, label]) => (
            <Group key={ring} gap="xs">
              <Badge color={ringColor(Number(ring))} variant="dot" size="xs">
                Ring {ring}
              </Badge>
              <Text size="xs" c="dimmed">{label}</Text>
            </Group>
          ))}
        </Group>
      </Card>

      {/* Search bar */}
      <Group gap="sm" mb="md">
        <TextInput
          ref={inputRef}
          placeholder="Enter config ID, e.g. tg-4-3-001, rs-4-1-004, tag:qol:dignity"
          leftSection={<IconSearch size={16} />}
          value={inputValue}
          onChange={(e) => setInputValue(e.currentTarget.value)}
          onKeyDown={handleKeyDown}
          style={{ flex: 1 }}
          size="md"
        />
        <Button
          onClick={handleLoad}
          disabled={!inputValue.trim()}
          leftSection={<IconArrowRight size={16} />}
          size="md"
        >
          Load Edges
        </Button>
      </Group>

      {/* History breadcrumb */}
      {history.length > 0 && (
        <Group gap="xs" mb="md" wrap="wrap">
          <Text size="xs" c="dimmed" fw={600}>History:</Text>
          {history.map((id, idx) => (
            <Group key={`${id}-${idx}`} gap={4}>
              <Button
                variant={id === activeConfigId ? 'filled' : 'subtle'}
                size="compact-xs"
                styles={{ root: { fontFamily: 'monospace' } }}
                onClick={() => handleHistoryClick(id)}
              >
                {id}
              </Button>
              {idx < history.length - 1 && (
                <IconArrowRight size={12} color="gray" />
              )}
            </Group>
          ))}
        </Group>
      )}

      {/* Tag info alert */}
      {isTagActive && (
        <Alert
          color="blue"
          icon={<IconInfoCircle size={16} />}
          mb="md"
        >
          This is a care vocabulary tag — reverse edges will show which configs reference it.
          Tag targets are defined in the care vocabulary.
        </Alert>
      )}

      {/* Empty state */}
      {!activeConfigId && (
        <Card withBorder p="xl">
          <Stack align="center" gap="sm">
            <IconBinaryTree size={48} color="gray" />
            <Text size="lg" fw={500}>Enter a config ID above to explore its care graph edges</Text>
            <Text size="sm" c="dimmed" ta="center">
              Examples: <Code>tg-4-3-001</Code> (task group), <Code>rs-4-1-004</Code> (ruleset),{' '}
              <Code>tag:qol:dignity</Code> (care vocabulary tag)
            </Text>
          </Stack>
        </Card>
      )}

      {/* Edge panels — both queries run in parallel */}
      {activeConfigId && (
        <EdgePanels
          configId={activeConfigId}
          onNavigate={handleNavigate}
        />
      )}
    </Container>
  );
}

export default ConfigEdgeBrowserPage;
